<template>
  <v-row justify="center">
    <v-col cols="12" md="6">
      <v-card outlined class="pa-6">

        <!-- idle -->
        <template v-if="uiState === 'idle'">
          <v-card-title class="justify-center text-h6 font-weight-bold mb-2">
            Convocation Payment
          </v-card-title>
          <v-card-text class="text-center">
            <p class="mb-1"><strong>Matriculation Number:</strong> {{ matric }}</p>
            <p class="mb-4"><strong>Convocation Year:</strong> {{ yearLabel }}</p>
            <v-btn color="primary" @click="initPayment">Proceed to Payment</v-btn>
          </v-card-text>
        </template>

        <!-- initializing -->
        <template v-if="uiState === 'initializing'">
          <v-card-text class="text-center py-8">
            <v-progress-circular indeterminate color="primary" size="56" class="mb-4" />
            <p class="text-subtitle-1">Preparing your invoice…</p>
          </v-card-text>
        </template>

        <!-- redirecting -->
        <template v-if="uiState === 'redirecting'">
          <v-card-text class="text-center py-8">
            <v-progress-circular indeterminate color="success" size="56" class="mb-4" />
            <p class="text-subtitle-1 font-weight-bold">
              Amount: ₦{{ invoiceAmount.toLocaleString() }}
            </p>
            <p class="text-body-2 grey--text">Redirecting to payment gateway…</p>
          </v-card-text>
        </template>

        <!-- verifying -->
        <template v-if="uiState === 'verifying'">
          <v-card-text class="text-center py-8">
            <v-progress-circular indeterminate color="primary" size="56" class="mb-4" />
            <p class="text-subtitle-1">Verifying your payment…</p>
          </v-card-text>
        </template>

        <!-- paid -->
        <template v-if="uiState === 'paid'">
          <v-card-text class="text-center py-6">
            <v-icon size="72" color="success" class="mb-4">mdi-check-circle</v-icon>
            <p class="text-h6 font-weight-bold green--text mb-2">Payment Successful</p>
            <p class="mb-1"><strong>Amount Paid:</strong> ₦{{ amountPaid.toLocaleString() }}</p>
            <p class="mb-4"><strong>Transaction ID:</strong> {{ transactionId }}</p>
            <v-btn text color="primary" @click="$router.push('/')">Return to Home</v-btn>
          </v-card-text>
        </template>

        <!-- pending -->
        <template v-if="uiState === 'pending'">
          <v-card-text class="text-center py-6">
            <v-icon size="72" color="warning" class="mb-4">mdi-alert-circle</v-icon>
            <p class="text-subtitle-1 font-weight-bold orange--text mb-2">Payment Pending</p>
            <p class="mb-4 text-body-2">
              Your payment has not been confirmed yet. Please retry verification or contact the registry.
            </p>
            <v-btn color="warning" class="mr-2" @click="retryVerify">Retry Verification</v-btn>
            <v-btn text color="primary" @click="$router.push('/')">Return to Home</v-btn>
          </v-card-text>
        </template>

        <!-- not_eligible -->
        <template v-if="uiState === 'not_eligible'">
          <v-card-text class="text-center py-6">
            <v-icon size="72" color="error" class="mb-4">mdi-account-remove</v-icon>
            <p class="text-subtitle-1 font-weight-bold red--text mb-2">Not Eligible</p>
            <p class="mb-4 text-body-2">
              Your matriculation number is not on the eligible list for this convocation year. Please contact the registry for assistance.
            </p>
            <v-btn text color="primary" @click="$router.push('/')">Go Back</v-btn>
          </v-card-text>
        </template>

        <!-- already_paid -->
        <template v-if="uiState === 'already_paid'">
          <v-card-text class="text-center py-6">
            <v-icon size="72" color="info" class="mb-4">mdi-information</v-icon>
            <p class="text-subtitle-1 font-weight-bold blue--text mb-2">Already Paid</p>
            <p class="mb-4 text-body-2">
              Our records show that payment has already been made for this convocation. Please contact the registry if you believe this is an error.
            </p>
            <v-btn text color="primary" @click="$router.push('/')">Return to Home</v-btn>
          </v-card-text>
        </template>

        <!-- configuration_error -->
        <template v-if="uiState === 'configuration_error'">
          <v-card-text class="text-center py-6">
            <v-icon size="72" color="error" class="mb-4">mdi-cog-off</v-icon>
            <p class="text-subtitle-1 font-weight-bold red--text mb-2">Payments Unavailable</p>
            <p class="mb-4 text-body-2">
              Convocation payments are currently disabled or not configured. Please check back later or contact the registry.
            </p>
            <v-btn text color="primary" @click="$router.push('/')">Return to Home</v-btn>
          </v-card-text>
        </template>

        <!-- gateway_error -->
        <template v-if="uiState === 'gateway_error'">
          <v-card-text class="text-center py-6">
            <v-icon size="72" color="error" class="mb-4">mdi-wifi-off</v-icon>
            <p class="text-subtitle-1 font-weight-bold red--text mb-2">Payment Gateway Error</p>
            <p class="mb-4 text-body-2">{{ gatewayMessage }}</p>
            <v-btn color="error" class="mr-2" @click="retry">Try Again</v-btn>
            <v-btn text color="primary" @click="$router.push('/')">Return to Home</v-btn>
          </v-card-text>
        </template>

      </v-card>
    </v-col>
  </v-row>
</template>

<script>
import Convocation from '@/shared/apis/convocation';

export default {
  name: 'ConvocationPaymentPage',

  data: () => ({
    uiState: 'idle',
    yearId: null,
    yearLabel: '',
    matric: '',
    invoiceAmount: 0,
    transactionId: '',
    amountPaid: 0,
    pendingReference: '',
    gatewayMessage: 'An error occurred with the payment gateway.',
  }),

  created() {
    const { yearId, yearLabel, matric, reference } = this.$route.query;

    if (reference) {
      this.verifyPayment(reference);
    } else if (yearId && matric) {
      this.yearId = yearId;
      this.yearLabel = yearLabel || yearId;
      this.matric = matric;
      this.uiState = 'idle';
    } else {
      this.$router.push('/');
    }
  },

  methods: {
    initPayment() {
      this.uiState = 'initializing';
      Convocation.initializePayment({ matric: this.matric, yearId: Number(this.yearId) })
        .then(res => {
          const { paymentLink, invoiceAmount } = res.data;
          this.invoiceAmount = invoiceAmount;
          this.uiState = 'redirecting';
          setTimeout(() => { window.location.href = paymentLink; }, 1500);
        })
        .catch(err => {
          const status = err.response?.status;
          if (status === 404) this.uiState = 'not_eligible';
          else if (status === 409) this.uiState = 'already_paid';
          else if (status === 503 || status === 400) this.uiState = 'configuration_error';
          else if (status === 502) {
            this.gatewayMessage = 'The payment gateway is temporarily unavailable. Please try again in a few minutes.';
            this.uiState = 'gateway_error';
          } else {
            this.gatewayMessage = err.response?.data?.message || 'Invoice generation failed. Please try again.';
            this.uiState = 'gateway_error';
          }
        });
    },

    verifyPayment(reference) {
      this.uiState = 'verifying';
      this.pendingReference = reference;
      Convocation.verifyPayment(reference)
        .then(res => {
          const { cleared, amountPaid, transactionId } = res.data;
          this.transactionId = transactionId;
          this.amountPaid = amountPaid;
          this.uiState = cleared ? 'paid' : 'pending';
        })
        .catch(err => {
          const status = err.response?.status;
          this.gatewayMessage = status === 404
            ? 'Payment record not found. Contact the registry if you have already paid.'
            : (err.response?.data?.message || 'Verification failed. Please retry.');
          this.uiState = 'gateway_error';
        });
    },

    retryVerify() {
      if (this.pendingReference) {
        this.verifyPayment(this.pendingReference);
      }
    },

    retry() {
      this.uiState = 'idle';
    },
  },
};
</script>
